import { useEffect, useMemo, useState } from 'react';
import momentTz from 'moment-timezone';
import { useQuery } from '@apollo/react-hooks';
import {
  getStaffMemberEventsV2,
  getStaffMemberNonWorkingHours,
  mapAvailabilityBlocksToEvents
} from 'utils/mappers/response/staff';
import { customizeRange } from 'utils/builders/calendar';
import { providerTemplateSummaryVar } from 'utils/cache/calendar/index';
import { effectiveTargetSessions } from 'utils/providerCapacity/effectiveTargetSessions';
import { buildTemplateSummary } from 'utils/mappers/response/providerTemplateSummary';
import { buildTemplateSeries } from 'utils/mappers/response/providerTemplate';
import { TIMEZONE } from 'utils/constants/default';
import { EVENT_STATUS } from 'utils/constants/status';
import {
  GET_PROVIDER_PROFILE_CALENDAR,
  GET_PROVIDER_SESSIONS_BY_IDS
} from 'api/graphql/v2/queries/Providers';
import { IProvider } from 'model/v2';
import { ICalendarEvent } from 'model/calendar/events';
import { View } from 'react-big-calendar';
import { CalendarMode } from './calendarModes';

/**
 * AXON-3585: one answer to "what does this provider's week contain?"
 *
 * Lifted out of ScheduleCalendar, which was doing this and drawing a calendar
 * in the same file. Separating them is what lets a second surface — the Ops
 * day grid, with clinicians in the columns — reuse the reshaping instead of
 * reimplementing it, and is what makes a fix here reach every surface rather
 * than only the one it was noticed on.
 *
 * Behaviour is unchanged from the inline version; the differences between
 * modes now come from the mode's own description rather than from a flag
 * consulted at each branch.
 */

const WEEKDAY_KEYS = ['sun', 'mon', 'tues', 'wed', 'thurs', 'fri', 'sat'];

const emptyWeek = () => ({
  sun: 0,
  mon: 0,
  tues: 0,
  wed: 0,
  thurs: 0,
  fri: 0,
  sat: 0,
  total: 0
});

interface Options {
  provider: IProvider;
  mode: CalendarMode;
  calendarDate: Date;
  calendarView: View;
}

export const useProviderTemplate = ({
  provider,
  mode,
  calendarDate,
  calendarView
}: Options) => {
  const [totalEvents, setTotalEvents] = useState<ICalendarEvent[]>([]);
  const [sessionData, setSessionData] = useState<any>(null);

  useQuery(GET_PROVIDER_SESSIONS_BY_IDS, {
    variables: { providerIds: [Number(provider.id)] },
    skip: !provider?.id || !mode.showsSessionHeaders,
    fetchPolicy: 'network-only',
    onCompleted: sessionsRes => {
      const s = sessionsRes?.getProviderSessionsByIds?.[0];
      if (s) setSessionData(s);
    }
  });

  const weekRange = customizeRange(calendarDate, calendarView);
  const queryRange = mode.lookAheadWeeks
    ? {
        start: weekRange.start,
        end: momentTz(weekRange.end)
          .add(mode.lookAheadWeeks, 'weeks')
          .toDate()
      }
    : weekRange;

  // The server is asked for a day either side of what is drawn, because the
  // bounds are this browser's midnights while the grid is the provider's. An
  // appointment in the first or last hours of the provider's week falls outside
  // the viewer's week as an instant, so without the margin it is never fetched
  // and silently missing. The extra day is discarded by the range test below.
  const FETCH_MARGIN_DAYS = 1;
  const fetchRange = {
    start: momentTz(queryRange.start)
      .subtract(FETCH_MARGIN_DAYS, 'days')
      .toDate(),
    end: momentTz(queryRange.end)
      .add(FETCH_MARGIN_DAYS, 'days')
      .toDate()
  };

  useQuery(GET_PROVIDER_PROFILE_CALENDAR, {
    fetchPolicy: 'no-cache',
    variables: {
      providerId: Number(provider.id),
      startDate: fetchRange.start,
      endDate: fetchRange.end,
      timeZone: TIMEZONE
    },
    skip: !provider?.id,
    onCompleted: data => {
      const availabilities = mapAvailabilityBlocksToEvents(
        data?.providerCalendarAvailabilities,
        data?.providerLocations,
        weekRange.start,
        mode.isTemplate
      );
      const events = data?.eventsByProvider || [];
      const userWorkingHours = data?.providerAvailabilityBlocks;

      setTotalEvents(
        getStaffMemberEventsV2(
          events,
          queryRange,
          provider?.clinic?.timezone
        ).concat(
          getStaffMemberNonWorkingHours(
            userWorkingHours?.workingHours,
            weekRange,
            provider?.id
          ).concat(availabilities)
        )
      );
    }
  });

  // The standing week: recurring care sessions (taken slots) plus every
  // availability slot type — Lunch, Meeting and Notes included, each with its
  // own label. Admin and one-off appointments are hidden. Cancelled instances
  // of a live series stay visible — the slot still belongs to that client next
  // week — but deleted ones do not, since that slot is genuinely free again.
  const standingWeekEvents = useMemo(() => {
    const slots = totalEvents.filter(e => e.type === 'availability');
    const series = totalEvents.filter(e => {
      if (e.type === 'na' || e.type === 'availability') return false;
      if (e.isAdmin) return false;
      if (e.status === EVENT_STATUS.deleted) return false;
      return !!e.recurrencePattern?.recurringUntil;
    });
    return buildTemplateSeries(series, weekRange.start).concat(slots);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalEvents, momentTz(weekRange.start).valueOf()]);

  const events = mode.foldsIntoStandingWeek ? standingWeekEvents : totalEvents;

  // Fallback session counts, used only when the backend has none stored.
  //
  // The standing week counts the slots it actually drew — one per series. It
  // must not count raw occurrences: the view fetches the displayed week plus
  // the look-ahead, so an ordinary weekly client appears seven times and would
  // be counted seven times, turning a day with free slots green.
  //
  // Every other department keeps the pre-AXON-3578 count, because that ticket
  // was DT-only and the old design shows this same number in its day headers.
  const bookedByDay = useMemo(() => {
    const counts: any = emptyWeek();
    const tally = (list: ICalendarEvent[]) =>
      list.forEach(e => {
        const key = WEEKDAY_KEYS[momentTz(e.start).day()];
        counts[key] += 1;
        counts.total += 1;
      });

    if (mode.foldsIntoStandingWeek) {
      // Do NOT gate on `e.type`. The provider-profile query selects no `type`
      // field — neither the Event nor ProviderEvent fragment asks for one, and
      // getStaffMemberEventsV2 does not add it — so every appointment arrives
      // without one. A `!e.type` guard here counted nothing at all and rendered
      // "0/6 Sessions" in red against a fully booked week.
      //
      // standingWeekEvents has already dropped availability slots, non-working
      // hours, admin appointments, deleted instances and one-offs, so what is
      // left is one entry per client series — exactly the number wanted.
      tally(
        standingWeekEvents.filter(e => e.type !== 'availability' && !!e.client)
      );
    } else {
      tally(
        totalEvents.filter(
          e => !!e.type && e.type !== 'na' && e.type !== 'availability'
        )
      );
    }
    return counts;
  }, [mode.foldsIntoStandingWeek, standingWeekEvents, totalEvents]);

  // Hand the sidebar the billable-hours totals and caseload for exactly the slots
  // drawn here, so the two can never disagree. Booked hours are not in here — they
  // come from the backend on providerCapacity. Cleared on unmount so another
  // provider's page starts blank rather than inheriting these numbers.
  useEffect(() => {
    if (!mode.publishesTemplateSummary) return;
    providerTemplateSummaryVar(
      buildTemplateSummary(standingWeekEvents, Number(provider?.id))
    );
  }, [mode.publishesTemplateSummary, standingWeekEvents, provider]);

  useEffect(
    () => () => {
      providerTemplateSummaryVar(null);
    },
    []
  );

  const providerWithSessions = {
    ...provider,
    providerCapacity: {
      ...provider?.providerCapacity,
      providerBookedSessions:
        sessionData?.bookedSessions ||
        provider?.providerCapacity?.providerBookedSessions ||
        bookedByDay,
      // No client-side fallback: booked hours have exactly one source, the
      // recompute that also produced providerBookedSessions. A locally derived
      // stand-in would be a second answer to the same question.
      providerBookedHours:
        sessionData?.bookedHours ||
        provider?.providerCapacity?.providerBookedHours,
      providerTargetSessions: effectiveTargetSessions(
        sessionData?.targetSessions ||
          provider?.providerCapacity?.providerTargetSessions,
        sessionData?.templateSessions
      )
    }
  };

  return { events, totalEvents, sessionData, providerWithSessions };
};
